import { useMemo } from 'react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  Area, 
  AreaChart,
  ResponsiveContainer
} from 'recharts';
import { format } from 'date-fns';
import { projectionsService } from '@/services/projectionsService';
import { aspirationsService } from '@/services/aspirationsService';
import { goalsService } from '@/services/goalsService';
import { listsService } from '@/services/listsService';
import { ProjectionEntity, TimeFrame, ChartType } from '@/types/projections';

interface ProjectionChartProps {
  entity: ProjectionEntity;
  timeframe: TimeFrame;
  type: ChartType;
}

export function ProjectionChart({ entity, timeframe, type }: ProjectionChartProps) {
  const chartData = useMemo(() => {
    if (type === 'burndown') {
      return projectionsService.getBurndownData(entity, timeframe === 'week' ? 7 : timeframe === 'month' ? 30 : timeframe === 'quarter' ? 90 : 365);
    } else if (type === 'velocity') {
      // Generate velocity data for the last 30 days
      const velocityData = [];
      for (let i = 29; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        const velocity = projectionsService.calculateVelocity(entity, i + 1);
        velocityData.push({
          date: format(date, 'MMM dd'),
          daily: velocity.dailyAverage,
          weekly: velocity.weeklyAverage / 7,
          trend: velocity.trend === 'increasing' ? 1 : velocity.trend === 'decreasing' ? -1 : 0
        });
      }
      return velocityData;
    } else {
      // Timeline view
      const data = projectionsService.generateChartData(entity, timeframe);
      // Group by date for better visualization
      const grouped = new Map<string, { actual: number; projected: number }>();
      
      data.dataPoints.forEach(point => {
        const dateKey = point.date.split('T')[0];
        if (!grouped.has(dateKey)) {
          grouped.set(dateKey, { actual: 0, projected: 0 });
        }
        const current = grouped.get(dateKey)!;
        if (point.type === 'actual') {
          current.actual += point.value;
        } else if (point.type === 'projected') {
          current.projected += point.value;
        }
      });
      
      return Array.from(grouped.entries()).map(([date, values]) => ({
        date: format(new Date(date), 'MMM dd'),
        actual: values.actual,
        projected: values.projected
      })).slice(-30); // Show last 30 data points
    }
  }, [entity, timeframe, type]);

  if (type === 'burndown') {
    return (
      <div>
        <h3 className="text-lg font-semibold mb-4">Burndown Chart</h3>
        <ResponsiveContainer width="100%" height={400}>
          <AreaChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis 
              dataKey="date" 
              tick={{ fontSize: 12 }}
              tickFormatter={(value) => format(new Date(value), 'MMM dd')}
            />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))', 
                border: '1px solid hsl(var(--border))' 
              }}
              labelFormatter={(value) => format(new Date(value), 'MMM dd, yyyy')}
            />
            <Legend />
            <Area 
              type="monotone" 
              dataKey="ideal" 
              stroke="#8b5cf6" 
              fill="#8b5cf6" 
              fillOpacity={0.3}
              name="Ideal Progress" 
            />
            <Area 
              type="monotone" 
              dataKey="actual" 
              stroke="#10b981" 
              fill="#10b981" 
              fillOpacity={0.3}
              name="Actual Progress" 
            />
            <Line 
              type="monotone" 
              dataKey="projected" 
              stroke="#f59e0b" 
              strokeDasharray="5 5"
              name="Projected" 
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  }

  if (type === 'velocity') {
    return (
      <div>
        <h3 className="text-lg font-semibold mb-4">Velocity Trends</h3>
        <ResponsiveContainer width="100%" height={400}>
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
            <XAxis dataKey="date" tick={{ fontSize: 12 }} />
            <YAxis tick={{ fontSize: 12 }} />
            <Tooltip 
              contentStyle={{ 
                backgroundColor: 'hsl(var(--card))', 
                border: '1px solid hsl(var(--border))' 
              }}
            />
            <Legend />
            <Line 
              type="monotone" 
              dataKey="daily" 
              stroke="#8b5cf6" 
              strokeWidth={2}
              name="Daily Average"
              dot={false}
            />
            <Line 
              type="monotone" 
              dataKey="weekly" 
              stroke="#10b981" 
              strokeWidth={2}
              name="Weekly Average (per day)"
              dot={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Timeline view - shows cumulative completion progress in priority order
  const timelineData = useMemo(() => {
    const projections = projectionsService.projectCompletionDates(entity);
    
    // Filter based on timeframe
    const now = new Date();
    let endDate = new Date();
    
    switch (timeframe) {
      case 'week':
        endDate = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        endDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
        break;
      case 'quarter':
        endDate = new Date(now.getTime() + 90 * 24 * 60 * 60 * 1000);
        break;
      case 'year':
        endDate = new Date(now.getTime() + 365 * 24 * 60 * 60 * 1000);
        break;
      case 'all':
      default:
        endDate = new Date(now.getTime() + 10 * 365 * 24 * 60 * 60 * 1000); // 10 years
        break;
    }
    
    // Filter projections by timeframe
    const filteredProjections = projections.filter(p => p.projectedDate <= endDate);
    
    // Sort by priority (items are already returned in priority order)
    const sortedProjections = filteredProjections.sort((a, b) => {
      // Get actual items to compare priorities
      if (entity === 'aspirations') {
        const aspirations = aspirationsService.getAspirationsSync();
        const itemA = aspirations.find(asp => asp.id === a.itemId);
        const itemB = aspirations.find(asp => asp.id === b.itemId);
        return (itemA?.priority || 999) - (itemB?.priority || 999);
      } else if (entity === 'goals') {
        const goals = goalsService.getAllGoals();
        const itemA = goals.find(goal => goal.id === a.itemId);
        const itemB = goals.find(goal => goal.id === b.itemId);
        return (itemA?.priority || 999) - (itemB?.priority || 999);
      } else {
        const lists = listsService.getAllLists();
        const itemA = lists.find(list => list.id === a.itemId);
        const itemB = lists.find(list => list.id === b.itemId);
        return (itemA?.priority || 999) - (itemB?.priority || 999);
      }
    });

    // Create cumulative completion data
    let cumulativeCompleted = 0;
    
    return sortedProjections.map((projection, index) => {
      // Add to cumulative count if item is completed (100% progress)
      if (projection.currentProgress >= 100) {
        cumulativeCompleted++;
      }
      
      return {
        name: projection.itemTitle.length > 20 
          ? projection.itemTitle.substring(0, 20) + '...' 
          : projection.itemTitle,
        priority: index + 1,
        completed: cumulativeCompleted,
        projected: index + 1, // Total items up to this point
        progress: projection.currentProgress,
        projectedDate: format(projection.projectedDate, 'MMM dd'),
        confidence: projection.confidenceLevel
      };
    });
  }, [entity, timeframe]);

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4">Completion Progress by Priority</h3>
      <p className="text-sm text-muted-foreground mb-4">
        Shows cumulative completion progress from highest priority (left) to lowest priority (right)
      </p>
      <ResponsiveContainer width="100%" height={400}>
        <LineChart data={timelineData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
          <XAxis 
            dataKey="name" 
            tick={{ fontSize: 10 }}
            angle={-45}
            textAnchor="end"
            height={80}
          />
          <YAxis tick={{ fontSize: 12 }} />
          <Tooltip 
            contentStyle={{ 
              backgroundColor: 'hsl(var(--card))', 
              border: '1px solid hsl(var(--border))' 
            }}
            formatter={(value, name, _props) => {
              if (name === 'completed') {
                return [`${value} completed`, 'Items Completed'];
              } else if (name === 'projected') {
                return [`${value} total`, 'Total Items'];
              }
              return [value, name];
            }}
            labelFormatter={(label, payload) => {
              if (payload && payload[0]) {
                const data = payload[0].payload;
                return `${label} (${data.progress}% complete, due ${data.projectedDate})`;
              }
              return label;
            }}
          />
          <Legend />
          <Line 
            type="monotone" 
            dataKey="projected" 
            stroke="#8b5cf6" 
            strokeWidth={2}
            name="Target Completion Line"
            strokeDasharray="5 5"
            dot={{ fill: '#8b5cf6', strokeWidth: 2 }}
          />
          <Line 
            type="monotone" 
            dataKey="completed" 
            stroke="#10b981" 
            strokeWidth={3}
            name="Actual Completion Progress"
            dot={{ fill: '#10b981', strokeWidth: 2, r: 4 }}
          />
        </LineChart>
      </ResponsiveContainer>
      <div className="mt-4 text-sm text-muted-foreground">
        <p>• <strong>Green line (solid):</strong> Your actual completion progress</p>
        <p>• <strong>Purple line (dashed):</strong> Target completion if all items were done in priority order</p>
        <p>• The gap between lines shows remaining work to complete all {entity}</p>
      </div>
    </div>
  );
}